import { z } from "zod";

import type { ChatAddMenuSendContext } from "./ChatAddMenuComposer";

export const conversationCreateOutboxKey = "merry.conversation-create-outbox.v1";

const outboxSchema = z
  .object({
    schemaVersion: z.literal(1),
    conversationId: z.string().min(1),
    shopBindingId: z.string(),
    userId: z.string().min(1),
    organizationId: z.string().min(1),
    draftFingerprint: z.string().min(1),
  })
  .strict();

export type ConversationCreateOutbox = z.infer<typeof outboxSchema>;

export function conversationCreateDraftFingerprint(
  text: string,
  context: Pick<ChatAddMenuSendContext, "resourceIds" | "extensions">,
) {
  const normalized = text.replace(/\s+/g, " ").trim();
  return JSON.stringify([
    normalized,
    [...context.resourceIds].sort(),
    [...(context.extensions ?? [])].map((extension) => extension.definitionId).sort(),
  ]);
}

export function parseConversationCreateOutbox(value: unknown) {
  const parsed = outboxSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

const memoryByStorage = new WeakMap<Storage, ConversationCreateOutbox>();

function readRawOutbox(storage: Storage) {
  try {
    const raw = storage.getItem(conversationCreateOutboxKey);
    if (raw) {
      const parsed = parseConversationCreateOutbox(JSON.parse(raw) as unknown);
      if (parsed) {
        memoryByStorage.set(storage, parsed);
        return parsed;
      }
      storage.removeItem(conversationCreateOutboxKey);
    }
  } catch {
    try {
      storage.removeItem(conversationCreateOutboxKey);
    } catch {
      /* 浏览器拒绝存储时保持空 outbox，不读取半成品。 */
    }
  }
  return memoryByStorage.get(storage) ?? null;
}

export function readConversationCreateOutbox(
  userId: string,
  organizationId: string,
  storage: Storage = sessionStorage,
) {
  const parsed = readRawOutbox(storage);
  if (!parsed || parsed.userId !== userId || parsed.organizationId !== organizationId) {
    return null;
  }
  return parsed;
}

export function writeConversationCreateOutbox(
  entry: ConversationCreateOutbox,
  storage: Storage = sessionStorage,
) {
  const parsed = outboxSchema.parse(entry);
  memoryByStorage.set(storage, parsed);
  try {
    storage.setItem(conversationCreateOutboxKey, JSON.stringify(parsed));
  } catch {
    /* 写入失败不把已成功的 201 报成未创建；本次返回值与内存副本仍携带会话 ID。 */
  }
}

export function clearConversationCreateOutbox(
  userId: string,
  organizationId: string,
  storage: Storage = sessionStorage,
) {
  try {
    const parsed = readRawOutbox(storage);
    if (!parsed || parsed.userId !== userId || parsed.organizationId !== organizationId) {
      return;
    }
    memoryByStorage.delete(storage);
    storage.removeItem(conversationCreateOutboxKey);
  } catch {
    memoryByStorage.delete(storage);
  }
}

export function resolveConversationCreateOutboxReuse(
  outbox: ConversationCreateOutbox | null,
  input: {
    draftFingerprint: string;
    organizationId: string;
    pendingConversationId?: string;
    userId: string;
  },
): ConversationCreateOutbox | null {
  if (!outbox) return null;
  if (outbox.userId !== input.userId || outbox.organizationId !== input.organizationId) {
    return null;
  }
  const pendingConversationId = input.pendingConversationId?.trim() ?? "";
  if (pendingConversationId) {
    return outbox.conversationId === pendingConversationId ? outbox : null;
  }
  return outbox.draftFingerprint === input.draftFingerprint ? outbox : null;
}
